import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaUserSecret, FaBriefcase, FaUniversity, FaFlask, FaGraduationCap, FaCamera, FaRedo, FaCrown, FaTrophy, FaLightbulb, FaEye } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { parseEducation } from '../utils/education';
import { useLanguage } from '../contexts/LanguageContext';

const TOTAL_ROUNDS = 8;
const HINTS_ORDER = ['institution', 'job', 'research', 'education', 'photo'];
const FREE_HINTS_PER_ROUND = 1;  // petunjuk gratis di awal tiap ronde
const EXTRA_HINT_QUOTA = 10;      // total kuota petunjuk tambahan sepanjang game
const BASE_SCORE = 100;
const HINT_COST = 15;
const TIME_BONUS_MAX = 30;
const TIME_PER_ROUND = 30;

const REACTIONS_CORRECT = ['🎉', '🔥', '💪', '✨', '🚀', '👏', '🌟', '⚡'];
const REACTIONS_WRONG = ['😅', '💀', '🥲', '😬', '🫠', '😵'];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const GuessAlumni = () => {
  const { t } = useLanguage();

  const [phase, setPhase] = useState('start'); // start | playing | result
  const [playerName, setPlayerName] = useState('');
  const [allAlumni, setAllAlumni] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [revealedHints, setRevealedHints] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [reaction, setReaction] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIME_PER_ROUND);
  const [loading, setLoading] = useState(false);
  const [extraHintsUsed, setExtraHintsUsed] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const roundStartRef = useRef(null);

  const fetchLeaderboard = useCallback(async () => {
    const { data } = await supabase
      .from('guess_alumni_scores')
      .select('*')
      .order('score', { ascending: false })
      .order('time_seconds', { ascending: true })
      .limit(20);
    setLeaderboard(data || []);
  }, []);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  // Timer
  useEffect(() => {
    if (phase !== 'playing' || showResult) return;
    if (timeLeft <= 0) { handleGuess(null); return; }
    timerRef.current = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, phase, showResult]);

  // Build rounds from alumni data
  const buildRounds = (alumni) => {
    // Filter alumni with enough data to make interesting clues
    const eligible = alumni.filter(a => a.name && (a.job || a.institution));
    if (eligible.length < 4) return [];

    const picked = shuffle(eligible).slice(0, Math.min(TOTAL_ROUNDS, eligible.length));
    return picked.map(target => {
      // Pick 3 wrong options (different from target)
      const others = shuffle(eligible.filter(a => a.id !== target.id)).slice(0, 3);
      const options = shuffle([target, ...others]);
      return {
        target,
        options,
        correctIndex: options.findIndex(o => o.id === target.id),
      };
    });
  };

  const startGame = async () => {
    if (!playerName.trim()) return;
    setLoading(true);

    if (allAlumni.length === 0) {
      const { data } = await supabase.from('alumni').select('*');
      setAllAlumni(data || []);
      const builtRounds = buildRounds(data || []);
      if (builtRounds.length === 0) { setLoading(false); return; }
      setRounds(builtRounds);
    } else {
      const builtRounds = buildRounds(allAlumni);
      if (builtRounds.length === 0) { setLoading(false); return; }
      setRounds(builtRounds);
    }

    setCurrentRound(0);
    setRevealedHints(HINTS_ORDER.slice(0, FREE_HINTS_PER_ROUND));
    setSelected(null);
    setShowResult(false);
    setScore(0);
    setCorrectCount(0);
    setStreak(0);
    setMaxStreak(0);
    setReaction('');
    setTotalTime(0);
    setTimeLeft(TIME_PER_ROUND);
    setExtraHintsUsed(0);
    startTimeRef.current = Date.now();
    roundStartRef.current = Date.now();
    setLoading(false);
    setPhase('playing');
  };

  const revealHint = () => {
    if (extraHintsUsed >= EXTRA_HINT_QUOTA) return;
    const nextHint = HINTS_ORDER.find(h => !revealedHints.includes(h));
    if (nextHint) {
      setRevealedHints(prev => [...prev, nextHint]);
      setExtraHintsUsed(prev => prev + 1);
    }
  };

  const getHintData = (alumni, hintKey) => {
    const edu = parseEducation(alumni.education);
    switch (hintKey) {
      case 'institution': return alumni.institution || null;
      case 'job': return alumni.job || null;
      case 'research': return alumni.research_interests || null;
      case 'education': {
        const parts = [];
        if (edu.s1_kampus) parts.push(`S1: ${edu.s1_kampus}${edu.s1_jurusan ? ` — ${edu.s1_jurusan}` : ''}`);
        if (edu.s2_kampus) parts.push(`S2: ${edu.s2_kampus}${edu.s2_jurusan ? ` — ${edu.s2_jurusan}` : ''}`);
        if (edu.s3_kampus) parts.push(`S3: ${edu.s3_kampus}${edu.s3_jurusan ? ` — ${edu.s3_jurusan}` : ''}`);
        return parts.length > 0 ? parts.join('\n') : null;
      }
      case 'photo': return alumni.photo_url || null;
      default: return null;
    }
  };

  const hintMeta = {
    institution: { icon: FaUniversity, label: 'Instansi', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
    job: { icon: FaBriefcase, label: 'Pekerjaan', color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
    research: { icon: FaFlask, label: 'Bidang Riset', color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
    education: { icon: FaGraduationCap, label: 'Pendidikan', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' },
    photo: { icon: FaCamera, label: 'Foto', color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-900/20' },
  };

  const handleGuess = (idx) => {
    if (showResult) return;
    clearTimeout(timerRef.current);
    setSelected(idx);
    setShowResult(true);

    const round = rounds[currentRound];
    const isCorrect = idx === round.correctIndex;
    const hintsUsed = revealedHints.length;
    const timeBonus = Math.max(0, Math.round((timeLeft / TIME_PER_ROUND) * TIME_BONUS_MAX));

    if (isCorrect) {
      const hintPenalty = hintsUsed * HINT_COST;
      const pts = Math.max(10, BASE_SCORE - hintPenalty + timeBonus);
      setScore(s => s + pts);
      setCorrectCount(c => c + 1);
      const newStreak = streak + 1;
      setStreak(newStreak);
      setMaxStreak(m => Math.max(m, newStreak));
      setReaction(REACTIONS_CORRECT[Math.floor(Math.random() * REACTIONS_CORRECT.length)]);
    } else {
      setStreak(0);
      setReaction(REACTIONS_WRONG[Math.floor(Math.random() * REACTIONS_WRONG.length)]);
    }

    setTimeout(() => {
      if (currentRound + 1 >= rounds.length) {
        const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
        setTotalTime(elapsed);
        // Calculate final score including this round
        const finalCorrect = correctCount + (isCorrect ? 1 : 0);
        let finalScore = score;
        if (isCorrect) {
          const hintPenalty = hintsUsed * HINT_COST;
          finalScore += Math.max(10, BASE_SCORE - hintPenalty + timeBonus);
        }
        finishGame(finalScore, elapsed, finalCorrect);
      } else {
        setCurrentRound(c => c + 1);
        setRevealedHints(HINTS_ORDER.slice(0, FREE_HINTS_PER_ROUND));
        setSelected(null);
        setShowResult(false);
        setReaction('');
        setTimeLeft(TIME_PER_ROUND);
        roundStartRef.current = Date.now();
      }
    }, 2000);
  };

  const finishGame = async (finalScore, elapsed, correct) => {
    setPhase('result');
    await supabase.from('guess_alumni_scores').insert({
      player_name: playerName.trim(),
      score: finalScore,
      total_rounds: rounds.length,
      correct_answers: correct,
      time_seconds: elapsed,
    });
    fetchLeaderboard();
  };

  const playAgain = () => {
    setPhase('start');
    setReaction('');
  };

  // ──── START SCREEN ────
  if (phase === 'start') {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">🕵️</div>
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1">{t('guess.title')}</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('guess.subtitle')}</p>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FaLightbulb className="text-amber-500" /> {t('guess.howToPlay')}
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
              <li className="flex items-start gap-2"><span>🕵️</span> {t('guess.rule1')}</li>
              <li className="flex items-start gap-2"><span>💡</span> {t('guess.rule2')}</li>
              <li className="flex items-start gap-2"><span>⏱️</span> {t('guess.rule3')}</li>
              <li className="flex items-start gap-2"><span>🏆</span> {t('guess.rule4')}</li>
            </ul>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('quiz.yourName')}</label>
            <input
              type="text"
              value={playerName}
              onChange={e => setPlayerName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && startGame()}
              placeholder={t('quiz.namePlaceholder')}
              maxLength={30}
              className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition text-sm"
            />
          </div>

          <button
            onClick={startGame}
            disabled={!playerName.trim() || loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-40 text-lg flex items-center justify-center gap-2"
          >
            {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🕵️'} {t('guess.startGame')}
          </button>
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaTrophy className="text-amber-500" /> {t('quiz.leaderboard')}
            </h3>
            <div className="space-y-2">
              {leaderboard.slice(0, 10).map((entry, i) => (
                <div key={entry.id} className={`flex items-center gap-3 p-2.5 rounded-xl ${i < 3 ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20' : 'bg-gray-50 dark:bg-dark-700'}`}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{
                    backgroundColor: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#e5e7eb',
                    color: i < 3 ? 'white' : '#6b7280',
                  }}>
                    {i < 3 ? <>{i === 0 ? '👑' : i === 1 ? '🥈' : '🥉'}</> : i + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex-1 truncate">{entry.player_name}</span>
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{entry.score}</span>
                  <span className="text-xs text-gray-400">{entry.time_seconds}s</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ──── PLAYING ────
  if (phase === 'playing') {
    const round = rounds[currentRound];
    const target = round.target;
    const progress = (currentRound / rounds.length) * 100;
    const timerPct = (timeLeft / TIME_PER_ROUND) * 100;
    const timerColor = timeLeft <= 5 ? 'bg-red-500' : timeLeft <= 15 ? 'bg-amber-500' : 'bg-emerald-500';
    const nextHint = HINTS_ORDER.find(h => !revealedHints.includes(h));
    const extraHintsRemaining = EXTRA_HINT_QUOTA - extraHintsUsed;
    const canRevealMore = nextHint && extraHintsRemaining > 0;
    const potentialScore = Math.max(10, BASE_SCORE - revealedHints.length * HINT_COST);

    return (
      <div className="space-y-4">
        {/* Top bar */}
        <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Ronde {currentRound + 1} / {rounds.length}
            </span>
            <div className="flex items-center gap-3">
              {streak >= 3 && <span className="text-xs font-bold text-orange-500 animate-pulse">🔥 x{streak}</span>}
              <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">⭐ {score}</span>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 dark:bg-dark-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Timer */}
        <div className="flex items-center gap-2">
          <span className={`text-xs ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>⏱️</span>
          <div className="flex-1 h-1.5 bg-gray-100 dark:bg-dark-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-1000 ${timerColor}`} style={{ width: `${timerPct}%` }} />
          </div>
          <span className={`text-xs font-bold min-w-[2rem] text-right ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-gray-500 dark:text-gray-400'}`}>
            {timeLeft}s
          </span>
        </div>

        {/* Mystery Card */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <FaUserSecret className="text-white text-xl" />
              <h3 className="text-white font-bold text-lg">Siapa Alumni Ini?</h3>
            </div>
            <div className="flex items-center justify-center gap-3 text-white/70 text-xs">
              <span>Skor potensial: <span className="font-bold text-white">{potentialScore}</span></span>
              <span>•</span>
              <span>Petunjuk: {revealedHints.length}/{HINTS_ORDER.length}</span>
              <span>•</span>
              <span>Kuota ekstra: <span className="font-bold text-white">{extraHintsRemaining}</span></span>
            </div>
          </div>

          {/* Reaction */}
          {reaction && showResult && (
            <div className="text-center text-4xl py-3" style={{ animation: 'bounceIn 0.4s ease' }}>{reaction}</div>
          )}

          {/* Revealed Hints */}
          <div className="p-4 space-y-3">


            {revealedHints.map(hintKey => {
              const meta = hintMeta[hintKey];
              const Icon = meta.icon;
              const data = getHintData(target, hintKey);
              if (!data) return null;

              if (hintKey === 'photo') {
                return (
                  <div key={hintKey} className={`${meta.bg} rounded-xl p-3 flex items-center gap-3`}>
                    <Icon className={`${meta.color} text-lg flex-shrink-0`} />
                    <div className="flex items-center gap-3">
                      <img
                        src={data}
                        alt="Hint"
                        className={`w-16 h-16 rounded-full object-cover border-2 border-white dark:border-dark-600 shadow-md ${!showResult ? 'blur-[6px]' : ''}`}
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-300 italic">
                        {!showResult ? 'Foto (disamarkan)' : target.name}
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={hintKey} className={`${meta.bg} rounded-xl p-3 flex items-start gap-3`}>
                  <Icon className={`${meta.color} text-lg flex-shrink-0 mt-0.5`} />
                  <div>
                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-0.5">{meta.label}</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 whitespace-pre-line">{data}</p>
                  </div>
                </div>
              );
            })}

            {/* Reveal Extra Hint Button */}
            {!showResult && nextHint && (
              canRevealMore ? (
                <button
                  onClick={revealHint}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-dashed border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all text-sm font-medium"
                >
                  <FaEye />
                  Buka Petunjuk ({meta_label(nextHint)}) — kuota ekstra: {extraHintsRemaining}
                </button>
              ) : (
                <div className="w-full text-center py-3 px-4 rounded-xl border-2 border-dashed border-gray-200 dark:border-dark-600 text-gray-400 dark:text-gray-500 text-sm">
                  🚫 Kuota petunjuk tambahan habis!
                </div>
              )
            )}
          </div>

          {/* Answer result overlay */}
          {showResult && (
            <div className={`mx-4 mb-2 p-3 rounded-xl text-center text-sm font-bold ${
              selected === round.correctIndex
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600'
                : 'bg-red-50 dark:bg-red-900/20 text-red-600'
            }`}>
              {selected === round.correctIndex
                ? `✅ Benar! +${Math.max(10, BASE_SCORE - revealedHints.length * HINT_COST + Math.max(0, Math.round((timeLeft / TIME_PER_ROUND) * TIME_BONUS_MAX)))} poin`
                : selected === null
                  ? `⏰ Waktu habis! Jawabannya: ${target.name}`
                  : `❌ Salah! Jawabannya: ${target.name}`
              }
            </div>
          )}

          {/* Options */}
          <div className="p-4 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {round.options.map((alumni, i) => {
              const isCorrectOpt = i === round.correctIndex;
              const isSelected = selected === i;
              let cls = 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-300 cursor-pointer';

              if (showResult) {
                if (isCorrectOpt) cls = 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-400';
                else if (isSelected && !isCorrectOpt) cls = 'border-red-400 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-400';
                else cls = 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 opacity-40';
              }

              return (
                <button
                  key={alumni.id}
                  onClick={() => handleGuess(i)}
                  disabled={showResult}
                  className={`text-left p-3.5 rounded-xl border-2 transition-all ${cls}`}
                >
                  <div className="flex items-center gap-3">
                    {showResult && alumni.photo_url ? (
                      <img src={alumni.photo_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                        showResult && isCorrectOpt ? 'bg-emerald-500 text-white'
                          : showResult && isSelected && !isCorrectOpt ? 'bg-red-500 text-white'
                          : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                      }`}>
                        {showResult && isCorrectOpt ? '✓' : showResult && isSelected ? '✗' : alumni.name.charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${showResult && isCorrectOpt ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-800 dark:text-gray-200'}`}>
                        {alumni.name}
                      </p>
                      {showResult && (
                        <p className="text-xs text-gray-400 truncate">{alumni.job || alumni.institution || ''}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Time's up */}
          {showResult && selected === null && (
            <p className="text-center text-red-500 text-sm font-bold pb-4 animate-pulse">⏰ Waktu habis!</p>
          )}
        </div>

        <style>{`
          @keyframes bounceIn { 0% { transform: scale(0); } 60% { transform: scale(1.3); } 100% { transform: scale(1); } }
        `}</style>
      </div>
    );
  }

  // ──── RESULT ────
  const maxPossible = rounds.length * (BASE_SCORE + TIME_BONUS_MAX);
  const pct = maxPossible > 0 ? Math.round((score / maxPossible) * 100) : 0;
  const grade = pct >= 80 ? { emoji: '🕵️‍♂️', label: 'Detektif Alumni Sejati!', color: 'from-yellow-400 to-amber-500' }
    : pct >= 60 ? { emoji: '🌟', label: 'Pengenal Alumni Hebat!', color: 'from-indigo-500 to-purple-500' }
    : pct >= 40 ? { emoji: '👍', label: 'Lumayan Kenal!', color: 'from-emerald-500 to-teal-500' }
    : pct >= 20 ? { emoji: '📚', label: 'Perlu Kenalan Lagi!', color: 'from-blue-500 to-cyan-500' }
    : { emoji: '💪', label: 'Yuk Kenali Alumninya!', color: 'from-gray-500 to-gray-600' };

  const playerRank = leaderboard.findIndex(e => e.score <= score);
  const rank = playerRank === -1 ? leaderboard.length + 1 : playerRank + 1;

  return (
    <div className="space-y-6">
      {/* Result Header */}
      <div className={`bg-gradient-to-r ${grade.color} rounded-2xl p-8 text-center`}>
        <div className="text-6xl mb-3">{grade.emoji}</div>
        <h2 className="text-2xl font-extrabold text-white mb-1">{grade.label}</h2>
        <p className="text-white/80">{playerName}</p>
      </div>

      {/* Score Card */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-100 dark:border-dark-700 p-6 text-center">
        <div className="text-5xl font-extrabold text-gray-900 dark:text-white mb-1">{score}</div>
        <p className="text-gray-400 text-sm mb-6">{t('quiz.points')}</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
            <p className="text-xl font-bold text-emerald-600">{correctCount}/{rounds.length}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.correct')}</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3">
            <p className="text-xl font-bold text-amber-600">{maxStreak} 🔥</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.bestStreak')}</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
            <p className="text-xl font-bold text-blue-600">{totalTime}s</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.time')}</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3">
            <p className="text-xl font-bold text-purple-600">#{rank}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.rank')}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={playAgain} className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-xl shadow-md transition-all flex items-center justify-center gap-2">
            <FaRedo /> {t('quiz.playAgain')}
          </button>
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FaCrown className="text-amber-500" /> {t('quiz.globalLeaderboard')}
          </h3>
          <div className="space-y-2">
            {leaderboard.map((entry, i) => {
              const isYou = entry.player_name === playerName.trim() && entry.score === score;
              return (
                <div key={entry.id} className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${isYou ? 'bg-indigo-50 dark:bg-indigo-900/30 ring-2 ring-indigo-400' : i < 3 ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20' : 'bg-gray-50 dark:bg-dark-700'}`}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{
                    backgroundColor: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#e5e7eb',
                    color: i < 3 ? 'white' : '#6b7280',
                  }}>
                    {i < 3 ? <>{i === 0 ? '👑' : i === 1 ? '🥈' : '🥉'}</> : i + 1}
                  </span>
                  <span className={`text-sm font-medium flex-1 truncate ${isYou ? 'text-indigo-700 dark:text-indigo-400 font-bold' : 'text-gray-700 dark:text-gray-200'}`}>
                    {entry.player_name} {isYou && <span className="text-xs">({t('quiz.you')})</span>}
                  </span>
                  <span className="text-xs text-gray-400">{entry.correct_answers}/{entry.total_rounds}</span>
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{entry.score}</span>
                  <span className="text-xs text-gray-400">{entry.time_seconds}s</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper for hint label
function meta_label(key) {
  const map = { institution: 'Instansi', job: 'Pekerjaan', research: 'Bidang Riset', education: 'Pendidikan', photo: 'Foto' };
  return map[key] || key;
}

export default GuessAlumni;
